import { Icon, type IconName } from '@dev-spendesk/grapes';

import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'src/core/common/hooks/useTranslation';

import { type Amenity } from '../../types';

type Props = {
  amenities: Amenity[];
};

const amenityIconMap: Record<Amenity['type'], IconName | null> = {
  parking: 'car',
  wifi: null,
  '24_hour_front_desk': 'person',
  adult_only: null,
  business_centre: 'shopping-cart',
  cash_machine: 'banknote',
  childcare_service: null,
  concierge: 'bell-desk',
  restaurant: 'fork-knife',
  room_service: null,
  spa: null,
  gym: null,
  accessibility_hearing: null,
  accessibility_mobility: null,
  laundry: null,
  lounge: null,
  pets_allowed: null,
  pool: null,
};

const amenityLabels: Record<Amenity['type'], string> = {
  parking: 'travel.propertyDetails.amenities.labels.parking',
  wifi: 'travel.propertyDetails.amenities.labels.wifi',
  '24_hour_front_desk': 'travel.propertyDetails.amenities.labels.frontDesk24h',
  adult_only: 'travel.propertyDetails.amenities.labels.adultOnly',
  business_centre: 'travel.propertyDetails.amenities.labels.businessCentre',
  cash_machine: 'travel.propertyDetails.amenities.labels.cashMachine',
  childcare_service: 'travel.propertyDetails.amenities.labels.childcareService',
  concierge: 'travel.propertyDetails.amenities.labels.concierge',
  restaurant: 'travel.propertyDetails.amenities.labels.restaurant',
  room_service: 'travel.propertyDetails.amenities.labels.roomService',
  spa: 'travel.propertyDetails.amenities.labels.spa',
  gym: 'travel.propertyDetails.amenities.labels.gym',
  accessibility_hearing:
    'travel.propertyDetails.amenities.labels.accessibilityHearing',
  accessibility_mobility:
    'travel.propertyDetails.amenities.labels.accessibilityMobility',
  laundry: 'travel.propertyDetails.amenities.labels.laundry',
  lounge: 'travel.propertyDetails.amenities.labels.lounge',
  pets_allowed: 'travel.propertyDetails.amenities.labels.petsAllowed',
  pool: 'travel.propertyDetails.amenities.labels.pool',
};

const getAmenityLabel = (type: Amenity['type'], t: TGlobalFunctionTyped) => {
  return t(amenityLabels[type] as keyof typeof t);
};

export const Amenities = ({ amenities }: Props) => {
  const { t } = useTranslation('global');

  if (amenities.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-16">
      <h3 className="title-l">{t('travel.propertyDetails.amenities.title')}</h3>
      <div className="grid grid-cols-3 gap-8">
        {amenities.map((amenity) => {
          const icon = amenityIconMap[amenity.type];
          return (
            <div key={amenity.type} className="flex items-center gap-8">
              {icon ? (
                <Icon
                  name={icon}
                  size="m"
                  className="text-content-secondary-bg-primary"
                />
              ) : (
                <div className="rounded-4 bg-background-secondary-default h-16 w-16" />
              )}
              <p className="body-m">{getAmenityLabel(amenity.type, t)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
